/* THE TWO SORTING RULES BOTH TABLES OBEY — stated once, here, because they are ONE rule each and
 * the roster and the exercise library would otherwise each carry their own copy.
 *
 * 🔴 "UNKNOWN SORTS LAST, WHICHEVER WAY THE COLUMN RUNS" IS THE RULE, AND IT IS NOT DECORATION.
 * Every alternative gives an absent value a RANK it has not earned:
 *   * treating it as 0 puts "nobody has rated this exercise" at the top of *easiest first*;
 *   * treating it as ∞ puts it at the top of *hardest first*;
 *   * letting it flip with the direction means one click hides it and the next click leads with it,
 *     so a coach looking for the unrated rows finds them by accident and never on purpose.
 * The absent value is the screen saying *I cannot answer for this row*, and an answer it cannot
 * give does not belong anywhere in the ranking. `docs/WEB-DESIGN-DIRECTION.md` §6.7 rules it for
 * difficulty and Last used by name; it is the same rule the roster's `activity` column already had.
 *
 * ⚠️ WHICH MEANS `nullsLast` MUST NOT BE MULTIPLIED BY THE DIRECTION. That is the mistake this
 * file exists to make impossible: the null branches return ±1 OUTRIGHT and only the comparison
 * between two REAL values is flipped.
 */

export type SortDir = "asc" | "desc";

/** +1 ascending, −1 descending. Multiply a real comparison by this; never a null branch. */
export function flip(dir: SortDir): 1 | -1 {
  return dir === "asc" ? 1 : -1;
}

/**
 * Compare two values that may be absent, with absence LAST in both directions.
 *
 * `cmp` is asked only when BOTH sides are present, and its result is the caller's to flip — so a
 * caller that forgets to flip gets a wrong ORDER, which is visible, rather than nulls that drift
 * to the top under one direction, which is not.
 */
export function nullsLast<T>(
  a: T | null | undefined,
  b: T | null | undefined,
  cmp: (x: T, y: T) => number,
): number {
  const ax = a ?? null;
  const bx = b ?? null;
  if (ax === null && bx === null) return 0;
  if (ax === null) return 1;
  if (bx === null) return -1;
  return cmp(ax, bx);
}

/** Numbers, smallest first. Pair with `flip` at the call site. */
export function numeric(x: number, y: number): number {
  return x < y ? -1 : x > y ? 1 : 0;
}

/**
 * Names, the way a person reads a list — case- and accent-insensitive.
 *
 * ⚠️ `sensitivity: "base"` on purpose: a library holding `Ring Row` and `ring row` must not put
 * one of them at the far end of the list because of a capital the coach did not think about.
 */
export function byName(x: string, y: string): number {
  return x.localeCompare(y, undefined, { sensitivity: "base" });
}

/**
 * A date as a COMPACT AGE — `today`, `3d`, `2w`, `5mo`.
 *
 * ⚠️ IT HAS NO ANSWER FOR "NOTHING", AND THAT IS DELIBERATE. The roster's empty cell means *this
 * screen is bounded to 90 days and cannot say*; the library's means *never used*. Two different
 * facts, so each caller supplies its own word and only the arithmetic is shared.
 *
 * ⚠️ A FUTURE DATE READS AS `today` RATHER THAN A NEGATIVE. The library counts a workout scheduled
 * for next Tuesday as used — it is plainly not dead weight — and `-6d` on a screen would read as a
 * bug rather than as a plan.
 */
export function compactAge(days: number): string {
  if (days <= 0) return "today";
  if (days === 1) return "1d";
  if (days < 14) return `${days}d`;
  if (days < 60) return `${Math.floor(days / 7)}w`;
  return `${Math.floor(days / 30)}mo`;
}
